



























import { Component, Watch, Prop } from "vue-property-decorator";
import {
  BaseStore,
  CanopyStore,
  FrameStore,
  LayoutStore,
  ShapeStore,
} from "@/mixins/store";
import { mixins } from "vue-class-component";
import ProductService from "@/services/product_service";
import { Auth } from "@/mixins/auth";
import { Roles } from "@/models/user/roles";
import { AuthError, GenericError } from "@/services/error_service";
import { Preset } from "@/models/configurator/configurator_options";
import PricingBreakdown from "@/components/order_form/PricingBreakdown.vue";
import { ConfigCreator } from "@/mixins/configurator";
import { EventBus, PRICING_SUSPEND, PRICING_RESUME } from "@/event-bus";

@Component({
  components: {
    PricingBreakdown,
  },
})
export default class Pricing extends mixins(
  ShapeStore,
  CanopyStore,
  BaseStore,
  LayoutStore,
  FrameStore,
  ConfigCreator,
  Auth
) {
  @Prop({ default: 1 }) qty!: number;
  protected productService = new ProductService();
  protected showPriceModal = false;
  protected itemPrice = { base: 0, umbrella: 0 };
  protected Preset = Preset;
  private priceRequestId = 0;
  private priceUpdateQueued = false;
  private pricingSuspended = false;

  created(): void {
    EventBus.$on(PRICING_SUSPEND, this.suspendPricing);
    EventBus.$on(PRICING_RESUME, this.resumePricing);
  }

  beforeDestroy(): void {
    EventBus.$off(PRICING_SUSPEND, this.suspendPricing);
    EventBus.$off(PRICING_RESUME, this.resumePricing);
  }

  /**
   * Hold off on price/SKU requests while a caller applies a multi-step config
   * change. Without this, a watcher firing mid-sequence sends a request built
   * from a partially-applied config (e.g. the stock reset's vent fabric had not
   * landed yet), and that stale price is what the user sees.
   */
  private suspendPricing(): void {
    this.pricingSuspended = true;
  }

  private async resumePricing(): Promise<void> {
    if (!this.pricingSuspended) return;
    this.pricingSuspended = false;
    await this.updatePriceAndSKU();
  }

  protected toggleModal(): void {
    this.showPriceModal = true;
  }

  @Watch("preset", { deep: true })
  protected async updatePreset(): Promise<void> {
    this.updatePriceAndSKU();
  }

  @Watch("umbrellaModel", { immediate: true, deep: true })
  protected updateUmbrella(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finish", { deep: true })
  protected updateFrameFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finial", { deep: true })
  protected updateFinial(): void {
    this.updatePriceAndSKU();
  }

  @Watch("finialFinish", { deep: true })
  protected updateFinialFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("bottomPole", { deep: true })
  protected updateBottomPole(): void {
    this.updatePriceAndSKU();
  }

  @Watch("spigot", { deep: true })
  protected updateSpigot(): void {
    this.updatePriceAndSKU();
  }

  @Watch("altaPagoda")
  protected updateAltaPagoda(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseFinish", { deep: true })
  protected updateBaseFinish(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseModel", { immediate: true, deep: true })
  protected updateBase(): void {
    this.updatePriceAndSKU();
  }

  @Watch("baseOptions", { immediate: true, deep: true })
  protected updateBaseOptions(): void {
    this.updatePriceAndSKU();
  }

  @Watch("allCanopyData", { deep: true })
  protected updateCanopy(): void {
    this.updatePriceAndSKU();
  }

  @Watch("valanceFabric", { deep: true })
  protected updateValance(): void {
    this.updatePriceAndSKU();
  }

  async updatePriceAndSKU(): Promise<void> {
    if (this.pricingSuspended) return;
    if (this.priceUpdateQueued) return;
    this.priceUpdateQueued = true;
    await this.$nextTick();
    this.priceUpdateQueued = false;
    await Promise.all([this.updatePrice(), this.updateSKU()]);
  }

  async updatePrice(): Promise<void> {
    const requestId = ++this.priceRequestId;
    try {
      const result = await this.productService.getTotal();
      if (requestId !== this.priceRequestId) return;
      this.itemPrice = { ...result };
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      }
      GenericError.popup(err.message);
    }
  }

  async updateSKU(): Promise<void> {
    const data = await this.transformDataToConfigFile();
    const sku = await this.productService.createFinishedUmbrellaSKU(data);
    this.$emit("updateSKU", sku);
  }

  protected close(): void {
    this.showPriceModal = false;
  }

  protected get tariffDivisor(): number {
    if (this.userRole !== Roles.Admin) return 1;
    const raw = process.env.VUE_APP_PRICE_SURCHARGE_VALUE_STRING || "0%";
    const pct = parseFloat(raw.split("%")[0]);
    if (isNaN(pct) || pct <= 0) return 1;
    return 1 + pct / 100;
  }

  get price(): string {
    const numberPrice =
      ((this.itemPrice.base + this.itemPrice.umbrella) *
        this.qty *
        this.msrpMultiplier) /
      this.tariffDivisor;
    return (Math.round(numberPrice * 100) / 100).toFixed(2);
  }

  get umbrellaPrice(): string {
    const numberPrice =
      (this.itemPrice.umbrella * this.msrpMultiplier) / this.tariffDivisor;
    return (Math.round(numberPrice * 100) / 100).toFixed(2);
  }

  get basePrice(): string {
    const numberPrice =
      (this.itemPrice.base * this.msrpMultiplier) / this.tariffDivisor;
    return (Math.round(numberPrice * 100) / 100).toFixed(2);
  }
}
